import store from '@/store'

/**
 * SSE client built on fetch + ReadableStream rather than EventSource.
 *
 * EventSource cannot set request headers, so authenticating it would mean putting
 * the JWT in the query string, where it lands in access logs and browser history.
 * Reading the stream through fetch keeps the token in the Authorization header.
 * The trade-off is that reconnection is ours to handle -- see connect() below.
 */

const RECONNECT_DELAYS_MS = [5000, 10000, 20000, 30000]

/**
 * Split a raw SSE chunk buffer into whole frames, returning the leftover partial.
 *
 * @param {string} buffer
 * @returns {{ frames: string[], rest: string }}
 */
export const splitFrames = buffer => {
  const parts = buffer.split(/\r?\n\r?\n/)
  const rest = parts.pop() ?? ''
  return { frames: parts.filter(frame => frame.trim() !== ''), rest }
}

/**
 * Parse one SSE frame into its event name and data payload.
 * Comment frames (": heartbeat") carry no event and are ignored by callers.
 *
 * @param {string} frame
 * @returns {{ event: string|null, data: string }}
 */
export const parseFrame = frame => {
  let event = null
  const dataLines = []

  frame.split(/\r?\n/).forEach(line => {
    if (line.startsWith(':')) return
    if (line.startsWith('event:')) {
      event = line.slice(6).trim()
      return
    }
    if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).replace(/^ /, ''))
    }
  })

  return { event, data: dataLines.join('\n') }
}

/**
 * Open an SSE connection and keep it open, reconnecting with backoff.
 *
 * @param {string} path      path under the API base, e.g. '/v3/dashboard/stream'
 * @param {Object} handlers
 * @param {(event: string, payload: any) => void} handlers.onEvent
 * @param {() => void} [handlers.onOpen]
 * @param {(error: Error) => void} [handlers.onError]
 * @returns {() => void} call to close the connection and stop reconnecting
 */
export const openStream = (path, { onEvent, onOpen, onError }) => {
  const controller = new AbortController()
  let closed = false
  let attempt = 0
  /** @type {number|null} */
  let retryTimer = null

  const connect = async () => {
    if (closed) return

    const { token } = store.state?.auth ?? {}

    try {
      const response = await fetch(`${process.env.VUE_APP_SERVER_DOMAIN}/api${path}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'text/event-stream',
        },
        signal: controller.signal,
      })

      if (!response.ok) throw new Error(`Stream failed with status ${response.status}`)
      if (!response.body) throw new Error('Streaming is not supported by this browser')

      attempt = 0
      if (onOpen) onOpen()

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      // Reads are inherently sequential here -- each chunk continues the previous frame.
      for (;;) {
        // eslint-disable-next-line no-await-in-loop
        const { value, done } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const { frames, rest } = splitFrames(buffer)
        buffer = rest

        frames.forEach(frame => {
          const { event, data } = parseFrame(frame)
          if (!event) return
          try {
            onEvent(event, data ? JSON.parse(data) : null)
          } catch (e) {
            if (onError) onError(new Error(`Malformed payload on "${event}" event`))
          }
        })
      }
    } catch (e) {
      if (closed || e?.name === 'AbortError') return
      if (onError) onError(e instanceof Error ? e : new Error('Stream error'))
    }

    // Reached on a stream error and on a clean server-side close (its max lifetime),
    // so either way we queue the next attempt.
    if (closed) return
    const delay = RECONNECT_DELAYS_MS[Math.min(attempt, RECONNECT_DELAYS_MS.length - 1)]
    attempt += 1
    retryTimer = setTimeout(connect, delay)
  }

  connect()

  return () => {
    closed = true
    if (retryTimer) clearTimeout(retryTimer)
    controller.abort()
  }
}
